import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import { clearNewCustomerCues } from './utils/newCustomersCue'
import { clearNewPartnerCues } from './utils/newPartnersCue'

Vue.use(Vuex)

export default new Vuex.Store({
  state() {
    return {
      userToken: '',
      email: '',
      id: '',
      accepted_eula: false,
      force_password_reset: false,
      // Import privileges from login/register (B3). Defaults false until granted in admin.
      can_import_csv: false,
      can_import_org: false,
      // Effective Bulk Data permission: org flag AND staff/admin/superuser.
      can_import_org_allowed: false,
      // Manage Gold (MG-1). Flag only — not implied by staff/superuser.
      can_edit_golden_record: false,
      // GTM Maestro admin room (EW-0). Flag only — not implied by staff/superuser.
      can_admin_gtm: false,
      has_gtm_play_seat: false,
      // TUBA BLAST (EW-T). Flag only — not implied by can_admin_gtm or superuser.
      can_tuba_blast: false,
      // AE is rostered on an active GTM (Radar GTM + named Matrix tab).
      has_my_gtms: false,
      // Blank employer after login/register — Profile prompts to set one.
      needs_employer: false,
      // K4 B: imported / unfinished mobile bind. Organic register stays false.
      needs_phone_proof: false,
      companies: [],
      matrixReloadNeeded: false,
      matrixReloadToken: 0,
      // Landing pane key (`matrix` | `manage` | `radar` | `import`). Matrix defers grid rebuild
      // while this is not `matrix` — DUX-1 rooms use the same flag.
      landingTab: 'matrix',
      rowData: [],
      columnDefs: [],
      localStorageItems: [
        'id',
        'email',
        'accepted_eula',
        'force_password_reset',
        'can_import_csv',
        'can_import_org',
        'can_import_org_allowed',
        'can_edit_golden_record',
        'can_admin_gtm',
        'can_tuba_blast',
        'has_gtm_play_seat',
        'has_my_gtms',
        'needs_employer',
        'needs_phone_proof',
      ],
      accountTypeUpdates: [],
      // Chatty — unread inbound DMs (Messaging toolbar). map + list with names.
      chatUnreadByPeer: {},
      chatUnreadPeers: [],
    }
  },
  mutations: {
    setChatUnread(state, payload) {
      const peers = (payload && payload.peers) || []
      const map = {}
      peers.forEach((p) => {
        const id = Number(p.id != null ? p.id : p)
        if (Number.isFinite(id)) map[id] = true
      })
      state.chatUnreadPeers = peers.map((p) => ({
        id: Number(p.id),
        name: p.name || `User ${p.id}`,
      }))
      state.chatUnreadByPeer = map
    },
    setChatUnreadByPeer(state, map) {
      // Live Firestore path — ids only; keep names if we already have them.
      const next = map && typeof map === 'object' ? { ...map } : {}
      state.chatUnreadByPeer = next
      const ids = Object.keys(next).map(Number).filter((n) => Number.isFinite(n) && next[n])
      const byId = {}
      ;(state.chatUnreadPeers || []).forEach((p) => { byId[p.id] = p })
      state.chatUnreadPeers = ids.map((id) => byId[id] || { id, name: `User ${id}` })
    },
    setToken(state, token) {
      state.userToken = token !== 'undefined' && token !== undefined ? token : state.userToken
      window.localStorage.setItem('token', token)
    },
    setUserData(state, payload = {}) {
      // Always replace provided fields — never keep the previous user's
      // identity when the new payload has a blank/missing personal_email.
      if (Object.prototype.hasOwnProperty.call(payload, 'email')) {
        state.email = payload.email == null ? '' : String(payload.email)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'id')) {
        const raw = payload.id
        state.id = raw === '' || raw == null ? '' : Number(raw)
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'accepted_eula')) {
        state.accepted_eula = !!payload.accepted_eula
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'force_password_reset')) {
        state.force_password_reset = !!payload.force_password_reset
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_import_csv')) {
        state.can_import_csv = !!payload.can_import_csv
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_import_org')) {
        state.can_import_org = !!payload.can_import_org
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_import_org_allowed')) {
        state.can_import_org_allowed = !!payload.can_import_org_allowed
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_edit_golden_record')) {
        state.can_edit_golden_record = !!payload.can_edit_golden_record
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_admin_gtm')) {
        state.can_admin_gtm = !!payload.can_admin_gtm
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'can_tuba_blast')) {
        state.can_tuba_blast = !!payload.can_tuba_blast
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'has_gtm_play_seat')) {
        state.has_gtm_play_seat = !!payload.has_gtm_play_seat
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'has_my_gtms')) {
        state.has_my_gtms = !!payload.has_my_gtms
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'needs_employer')) {
        state.needs_employer = !!payload.needs_employer
      }
      if (Object.prototype.hasOwnProperty.call(payload, 'needs_phone_proof')) {
        state.needs_phone_proof = !!payload.needs_phone_proof
      }
      for (let item of state.localStorageItems) {
        window.localStorage.setItem(item, state[`${item}`])
      }
    },
    logout(state) {
      state.userToken = ''
      state.email = ''
      state.id = ''
      state.accepted_eula = false
      state.force_password_reset = false
      state.can_import_csv = false
      state.can_import_org = false
      state.can_import_org_allowed = false
      state.can_edit_golden_record = false
      state.can_admin_gtm = false
      state.can_tuba_blast = false
      state.has_gtm_play_seat = false
      state.has_my_gtms = false
      state.needs_employer = false
      state.needs_phone_proof = false
      state.companies = []
      state.rowData = []
      state.columnDefs = []
      state.accountTypeUpdates = []
      state.chatUnreadByPeer = {}
      state.chatUnreadPeers = []
      state.matrixReloadNeeded = false
      state.landingTab = 'matrix'
      window.localStorage.removeItem('token')
      for (let item of state.localStorageItems) {
        window.localStorage.removeItem(item)
      }
      clearNewCustomerCues()
      clearNewPartnerCues()
    },
    setCompanyList(state, companyList) {
      state.companies = [...companyList]
    },
    setLandingTab(state, tab) {
      state.landingTab = tab || 'matrix'
    },
    setMatrixReloadNeeded(state, flag) {
      state.matrixReloadNeeded = !!flag
      // Only bump while Matrix is the active Landing pane. A token bump
      // re-renders a hidden keep-alive grid and has thrown over Manage.
      if (flag && state.landingTab === 'matrix') {
        state.matrixReloadToken += 1
      }
    },
    setRowData(state, rowData) {
      state.rowData = [...rowData]
    },
    setColumnData(state, columnData) {
      state.columnDefs = [...columnData]
    },
    updateAccountType(state, payload) {
      state.accountTypeUpdates.push(payload)
    },
    clearAccountTypeUpdates(state) {
      state.accountTypeUpdates = [];
    }
  },
  getters: {
    isLoggedIn(state) {
      return state.userToken !== ''
    },
    hasAcceptedEula(state) {
      return state.accepted_eula
    },
    requiresPasswordReset(state) {
      return state.force_password_reset
    },
    canImportCsv(state) {
      return !!state.can_import_csv
    },
    canImportOrg(state) {
      return !!state.can_import_org_allowed
    },
    canEditGoldenRecord(state) {
      return !!state.can_edit_golden_record
    },
    canAdminGtm(state) {
      return !!state.can_admin_gtm
    },
    canTubaBlast(state) {
      return !!state.can_tuba_blast
    },
    canOpenGtmMaestro(state) {
      return !!state.can_admin_gtm || !!state.has_gtm_play_seat
    },
    hasMyGtms(state) {
      return !!state.has_my_gtms
    },
    needsEmployer(state) {
      return !!state.needs_employer
    },
    needsPhoneProof(state) {
      return !!state.needs_phone_proof
    },
    baseURL() {
      // Prefer runtime config.js in deployed Cloud Run; fall back for local/.env builds.
      if (typeof config !== 'undefined' && config && config.VUE_APP_ENV_BASE_URL) {
        return config.VUE_APP_ENV_BASE_URL
      }
      return process.env.VUE_APP_API_URL || 'http://localhost:8000'
    },
    userName(state) {
      return `${state.email}`
    },
    getAccountTypeUpdates(state) {
      return state.accountTypeUpdates
    }
  },
  actions: {
    /**
     * Restore token + cached profile from localStorage, then reconcile id/email
     * against /api/user/me/ so a stale `id` cannot blank Matrix/Manage.
     */
    async persistLogin (context) {
      const token = window.localStorage.getItem('token')
      if (token && token !== 'null' && token !== 'undefined') {
        context.commit('setToken', token)
        const stored_eula_status = window.localStorage.getItem('accepted_eula')
        const storedId = window.localStorage.getItem('id')
        const storedEmail = window.localStorage.getItem('email')
        context.commit('setUserData', {
          accepted_eula: stored_eula_status === 'true',
          email: storedEmail && storedEmail !== 'null' ? storedEmail : '',
          id: storedId && storedId !== 'null' ? Number(storedId) : '',
          force_password_reset: window.localStorage.getItem('force_password_reset') === 'true',
          can_import_csv: window.localStorage.getItem('can_import_csv') === 'true',
          can_import_org: window.localStorage.getItem('can_import_org') === 'true',
          can_import_org_allowed: window.localStorage.getItem('can_import_org_allowed') === 'true',
          can_edit_golden_record: window.localStorage.getItem('can_edit_golden_record') === 'true',
          can_admin_gtm: window.localStorage.getItem('can_admin_gtm') === 'true',
          can_tuba_blast: window.localStorage.getItem('can_tuba_blast') === 'true',
          has_gtm_play_seat: window.localStorage.getItem('has_gtm_play_seat') === 'true',
          has_my_gtms: window.localStorage.getItem('has_my_gtms') === 'true',
          needs_employer: window.localStorage.getItem('needs_employer') === 'true',
          needs_phone_proof: window.localStorage.getItem('needs_phone_proof') === 'true',
        })
        await context.dispatch('reconcileSession')
      } else {
        context.commit('logout')
      }
    },
    /**
     * Align Vuex id/email/flags with the Authorization token (server truth).
     * No full logout — fixes multi-device / stale localStorage without re-auth.
     */
    async reconcileSession (context) {
      const token = context.state.userToken
      if (!token) return
      try {
        const res = await axios.get(`${context.getters.baseURL}/api/user/me/`, {
          headers: { Authorization: `Token ${token}` },
        })
        const d = res.data || {}
        if (d.id == null) return
        context.commit('setUserData', {
          id: d.id,
          email: d.email || d.work_email || context.state.email,
          accepted_eula: !!d.accepted_eula,
          force_password_reset: !!d.force_password_reset,
          can_import_csv: !!d.can_import_csv,
          can_import_org: !!d.can_import_org,
          can_import_org_allowed: !!d.can_import_org_allowed,
          can_edit_golden_record: !!d.can_edit_golden_record,
          can_admin_gtm: !!d.can_admin_gtm,
          can_tuba_blast: !!d.can_tuba_blast,
          has_gtm_play_seat: !!d.has_gtm_play_seat,
          has_my_gtms: !!d.has_my_gtms,
          needs_employer: !!d.needs_employer,
          needs_phone_proof: !!d.needs_phone_proof,
        })
      } catch (err) {
        const status = err && err.response && err.response.status
        if (status === 401) {
          context.commit('logout')
        }
        // Network / 5xx: keep cached identity; Matrix may still load via token.
      }
    },
    triggerAccountTypeUpdate({ commit }, payload) {
      commit('updateAccountType', payload);
    }
  }
})
